import queryString from 'query-string';
import { call, put, select } from 'redux-saga/effects';

import { insertCacheItem } from '../actions/cacheActions';

import localCache from '../../../core/js/utils/cacheUtils';
import { debug, isVerbose, verbose, warn, error as logError } from '../utils/logger';

import {
    ALL_SCOPES
} from '../constants';
import { checkLogin, performReauth, getGuestToken } from '../sagas/loginSagas';

import { urbnrest as restClient } from '../rest/urbnrest';

const MAX_WISHLISTS_ERROR = 'MAX_WISH_LISTS_EXCEEDED';
const urbnrest = restClient();

/**
 * Make a REST request
 *
 * Usage: yield call(makeRequest(state, { route });
 *
 * Checks browser localStorage for cached value and updates the cache with the value if:
 *
 * 1. route specifies a cacheTTL property value (number of seconds from now)
 * 2. HTTP response contains a Cache-Control header with a max-age value (number of seconds from now)
 *
 * If either condition is true, the cached value will have the specified expiration time
 *
 * The responses are cached with a key that is either:
 * 1. the route's cacheKey property
 * 2. the route's path
 *
 * @see https://github.com/cujojs/rest/blob/master/docs/interfaces.md
 *
 * @param {Object} route rest.js route object
 * @param {String} route.path - URL for the service
 * @param {String} route.method - HTTP Verb.  GET/POST/PUT/DELETE/etc
 * @param {String} route.scope - Scope required for the service call
 * @param {Boolean} route.localStorage - true/false value for whether to store in localStorage
 * @param {Boolean} route.reduxCache - true/false value for whether to store in redux cache
 * @param {Number} route.cacheTTL - An override for the ttl values found in the response headers
 * @param {String} route.cacheKey - An override for the cachekey value of the item
 * @returns {Object} the response data returned from specified route
 */
export function* makeRequest(route) {
    if (!route) {
        warn('makeRequest saga called with an undefined route.');
        return null;
    }

    if (!route.scope) {
        // Assuming that no scope defined means that any scope can use this.
        route.scope = ALL_SCOPES;
    }

    if (!route.headers) {
        route.headers = {};
    }

    yield call(checkLogin, route);
    const cacheResponse = yield call(checkCache, route);

    if (cacheResponse) {
        return cacheResponse;
    }

    const restResponse = yield call(makeURBNRestCall, urbnrest, route);
    yield call(setCache, restResponse, route);
    const restResponseEntity = _.get(restResponse, 'entity');

    if (restResponseEntity) {
        return restResponseEntity;
    }

    return null;
}

export function* makeURBNRestCall(restjsClient, route, failedAfterReauth = false, failedAfterNewGuestToken = false) {
    try {
        debug(`Inside makeURBNRestCall to call ${route.path}`);
        const restResponse = yield call(restjsClient, route);
        return restResponse;
    } catch (originalErrResponse) {
        if (isResponseCode(originalErrResponse, 403) &&
            !(_.get(originalErrResponse, 'entity.code') === MAX_WISHLISTS_ERROR) &&
            !failedAfterReauth) {
            try {
                debug('Performing reauth');
                const reauthedTokenSession = yield call(performReauth);
                debug(`Retrying makeURBNRestCall to call ${route.path}`);
                route.session = reauthedTokenSession;
                const secondResponse = yield call(makeURBNRestCall, restjsClient, route, true);
                return secondResponse;
            } catch (reauthErrResponse) {
                if (isResponseCode(reauthErrResponse, 403) && !failedAfterNewGuestToken) {
                    debug('Reauth failed, getting guest token');
                    const guestTokenSession = yield call(getGuestToken);
                    debug(`Retrying makeURBNRestCall with guest token to call ${route.path}`);
                    route.session = guestTokenSession;
                    const responseWithNewGuestToken = yield call(makeURBNRestCall, restjsClient, route, true, true);
                    return responseWithNewGuestToken;
                }

                throw reauthErrResponse;
            }
        } else {
            logError('Unknown Error: makeURBNRestCall', originalErrResponse);
            const failSilently = _.get(route, 'failSilently', false);

            if (!failSilently) {
                throw originalErrResponse;
            }

            return null;
        }
    }
}

function isResponseCode(response, expectedCode) {
    if (_.get(response, 'status.code') === expectedCode) {
        return true;
    } else if (_.get(response, 'statusCode') === expectedCode) {
        return true;
    }

    return false;
}

/**
 * Check which cache mechanism to check based on route property.
 * If cache value has not expired, return cache value, else return undefined.
 *
 * @param {Object} route rest.js route
 * @param {String} route.path - URL for the service
 * @param {String} route.method - HTTP Verb.  GET/POST/PUT/DELETE/etc
 * @param {String} route.scope - Scope required for the service call
 * @param {Boolean} route.localStorage - true/false value for whether to store in localStorage
 * @param {Boolean} route.reduxCache - true/false value for whether to store in redux cache
 * @param {Number} route.cacheTTL - An override for the ttl values found in the response headers
 * @param {String} route.cacheKey - An override for the cachekey value of the item
 * @returns {Object} the cached value (if any)
 */
export function* checkCache(route) {
    const state = yield select();
    if (route) {
        const cacheKey = yield call(getCacheKeyFromRoute, route, state);
        if (route.reduxCache) {
            if (cacheKey) {
                debug(`Searching redux store for key: ${cacheKey}`);

                const cachedValue = yield call([localCache, localCache.getReduxCache], state, cacheKey);

                if (cachedValue) {
                    if (isVerbose()) {
                        verbose(`Value in redux store for key ${cacheKey}: ${JSON.stringify(cachedValue)}`);
                    }

                    return cachedValue;
                }

                debug(`Redux miss for key ${cacheKey}`);
            }
        }

        if (route.localStorage) {
            if (cacheKey) {
                debug(`Searching local storage for key: ${cacheKey}`);

                const cachedValue = yield call([localCache, localCache.get], cacheKey);

                if (cachedValue) {
                    if (isVerbose()) {
                        verbose(`Value in local storage for key ${cacheKey}: ${JSON.stringify(cachedValue)}`);
                    }
                    return cachedValue;
                }

                debug(`Local store miss for key ${cacheKey}`);
            }
        }
    }

    return null;
}

/**
 * Check which cache mechanism to save to based on route property.
 * If cache location is selected save there with expires value based on ttl.
 * If no cache location is selected just return result.
 * @param {Object} response rest.js response object
 * @param {Object} route rest.js route object
 * @param {String} route.path - URL for the service
 * @param {String} route.method - HTTP Verb.  GET/POST/PUT/DELETE/etc
 * @param {String} route.scope - Scope required for the service call
 * @param {Boolean} route.localStorage - true/false value for whether to store in localStorage
 * @param {Boolean} route.reduxCache - true/false value for whether to store in redux cache
 * @param {Number} route.cacheTTL - An override for the ttl values found in the response headers
 * @param {String} route.cacheKey - An override for the cachekey value of the item
 */
export function* setCache(response, route) {
    const state = yield select();
    if (response && route) {
        const cacheKey = getCacheKeyFromRoute(route, state);

        if (cacheKey) {
            const maxAgeRegExp = /max-age=(\d+)/;
            const match = String(response.headers['Cache-Control']).match(maxAgeRegExp);

            // use custom cacheTTL value in route if defined, otherwise respect response's Cache-Control header if any
            let ttl = route.cacheTTL || 0;

            if (ttl === 0) {
                if (match && match.length > 0) {
                    ttl = Number(match[1]);
                }
            }

            if (ttl > 0) {
                if (route.localStorage) {
                    debug(`Caching response in local storage under key: ${cacheKey}`);
                    localCache.set(cacheKey, response.entity, ttl);
                }
                if (route.reduxCache) {
                    debug(`Caching response in redux cache under key: ${cacheKey}`);
                    const cacheItem = localCache.setReduxCache(cacheKey, response.entity, ttl);
                    yield put(insertCacheItem(cacheKey, cacheItem));
                }
            }
        }
    }
}

/**
 * Helper function to retrieve cacheKey from route object.
 * @param {Object} route rest.js route object
 * @param {String} route.path  - URL for the service
 * @param {String} route.method - HTTP Verb.  GET/POST/PUT/DELETE/etc
 * @param {String} route.scope - Scope required for the service call
 * @param {Boolean} route.localStorage - true/false value for whether to store in localStorage
 * @param {Boolean} route.reduxCache - true/false value for whether to store in redux cache
 * @param {Number} route.cacheTTL - An override for the ttl values found in the response headers
 * @param {String} route.cacheKey - An override for the cachekey value of the item
 * @param {object} state - State of the redux store
 * @returns {String} the route's cache key with cachebuster or null if no route provided
 */
export function getCacheKeyFromRoute(route, state) {
    if (!route) {
        return null;
    }

    let jenkinsDeploymentNumber = '';

    // state.core.cacheBuster is set to the Jenkins deployment number
    if (state && state.core && state.core.cacheBuster) {
        jenkinsDeploymentNumber = state.core.cacheBuster;
    }

    if (route.cacheKey) {
        return `${route.cacheKey}${jenkinsDeploymentNumber}`;
    } else if (route.params) {
        return `${route.path}${queryString.stringify(route.params)}${jenkinsDeploymentNumber}`;
    }

    return `${route.path}${jenkinsDeploymentNumber}`;
}
